<template>
  <div class="pt-1">
    <cohort-list-add-new :is-add-new-cohort-sidebar-active.sync="isAddNewCohortSidebarActive" @refetch-data="refetchData" />

    <div class="content-px mb-1">
      <cohort-list-filters
        :status-filter.sync="statusFilter"
        :status-options="statusOptions"
        :create-at-start-filter.sync="createAtStartFilter"
        :create-at-end-filter.sync="createAtEndFilter"
      />
    </div>

    <div class="content-px mb-1">
      <b-row>
        <b-col cols="12" sm="6" md="6">
          <div class="d-flex align-items-center justify-content-start flex-wrap flex-sm-nowrap" style="gap: 0.675rem">
            <div style="max-width: 18rem; min-width: 15rem; width: 100%">
              <b-form-input v-model="searchQuery" class="drop-shadow-default w-full" placeholder="Search..." />
            </div>
          </div>
        </b-col>

        <b-col cols="12" sm="6" md="6" class="d-flex align-items-center justify-content-end">
          <div class="d-flex align-items-center justify-content-end">
            <b-button v-if="false" variant="info" :to="{ name: 'apps-accounting-receivable-cohort-import' }" class="mr-1">
              <span class="text-nowrap">Import Cohort</span>
            </b-button>
            <b-button variant="primary" @click="isAddNewCohortSidebarActive = true">
              <span class="text-nowrap">Create Cohort</span>
            </b-button>
          </div>
        </b-col>
      </b-row>
    </div>

    <!-- Table Container Card -->
    <div no-body class="mb-0">
      <b-table
        ref="refCohortListTable"
        class="position-relative mb-0"
        :items.sync="fetchCohorts"
        responsive
        :fields="tableColumns"
        :filter.sync="searchQuery"
        :filterByFormatted="true"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No matching records found"
        :sort-desc.sync="isSortDirDesc"
        :current-page.sync="currentPage"
        :per-page.sync="perPage"
      >
        <!-- Column: Cohort name -->
        <template #cell(name)="data">
          <div class="text-nowrap" v-if="data.item.session">
            <div
              v-if="data.item.session.type === 'pathway' && data.item.session.pathway"
              class="d-flex align-items-center text-nowrap"
              style="gap: 0.625rem"
            >
              <b-avatar
                size="36"
                :src="data.item.session.pathway.avatar"
                :text="avatarText(data.item.session.pathway.name)"
                :variant="`light-info`"
                :to="{ name: 'apps-accounting-receivable-cohort-view', params: { id: data.item.id } }"
                style="margin-top: 2px"
                rounded
              />
              <div class="d-flex flex-column" style="gap: 0">
                <b-link
                  :to="{ name: 'apps-accounting-receivable-cohort-view', params: { id: data.item.id } }"
                  class="font-weight-bold text-nowrap text-white mb-0"
                >
                  {{ resolvePathwayCohortName(data.item.session, 'MMM Do YYYY') }}
                </b-link>
                <b-link
                  :to="{ name: 'apps-accounting-receivable-cohort-view', params: { id: data.item.id } }"
                  class="d-flex align-items-center text-muted text-nowrap"
                >
                  <lucide-icon name="Library" style="width: 14px; height: 14px; margin-right: 1px" />
                  <small> {{ data.item.session.pathway.name }}</small>
                </b-link>
              </div>
            </div>
            <div
              v-if="data.item.session.type === 'course' && data.item.session.course"
              class="d-flex align-items-center text-nowrap"
              style="gap: 0.625rem"
            >
              <b-avatar
                size="36"
                :src="data.item.session.course.avatar"
                :text="avatarText(data.item.session.course.name)"
                :variant="`light-info`"
                :to="{ name: 'apps-accounting-receivable-cohort-view', params: { id: data.item.id } }"
                style="margin-top: 2px"
                rounded
              />
              <div class="d-flex flex-column" style="gap: 0">
                <b-link
                  :to="{ name: 'apps-accounting-receivable-cohort-view', params: { id: data.item.id } }"
                  class="font-weight-bold text-nowrap text-white mb-0"
                >
                  {{ resolveCourseSectionName(data.item.session, 'MMM Do YYYY') }}
                </b-link>
                <b-link
                  :to="{ name: 'apps-accounting-receivable-cohort-view', params: { id: data.item.id } }"
                  class="d-flex align-items-center text-muted text-nowrap"
                >
                  <lucide-icon name="Book" style="width: 14px; height: 14px; margin-right: 1px" />
                  <small> {{ data.item.session.course.name }}</small>
                </b-link>
              </div>
            </div>
          </div>
          <div v-else>
            <b-link
              :to="{ name: 'apps-accounting-receivable-cohort-view', params: { id: data.item.id } }"
              class="font-weight-bold d-block text-nowrap text-white"
            >
              {{ data.item.name }}
            </b-link>
            <span class="text-muted"> Cohort Does Not Have Session </span>
          </div>
        </template>

        <!-- Column: total -->
        <template #cell(total)="data">
          <span class="text-nowrap">{{ money(data.item.total) }}</span>
        </template>

        <!-- Column: balance -->
        <template #cell(totalPaid)="data">
          <span class="text-nowrap">{{ money(data.item.totalPaid) }}</span>
        </template>

        <!-- Column: balance -->
        <template #cell(balance)="data">
          <span class="text-nowrap">{{ money(data.item.balance) }}</span>
        </template>

        <!-- Column: Program Start Date -->
        <template #cell(program.startDate)="data">
          {{ moment.utc(data.item.program.startDate).format('L') }}
        </template>

        <!-- Column: Program End Date -->
        <template #cell(program.endDate)="data">
          {{ moment.utc(data.item.program.endDate).format('L') }}
        </template>

        <!-- Column: Session -->
        <template #cell(createdAt)="data">
          {{ moment.utc(data.item.createdAt).format('L') }}
        </template>

        <!-- Column: Status -->
        <template #cell(status)="data">
          <b-badge :variant="`${resolveCohortStatusVariant(data.item.status)}`" class="text-capitalize">
            {{ title(data.item.status) }}
          </b-badge>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-dropdown variant="primary" class="dropdown-edit-button">
            <template #button-content>
              <div class="p-50">
                <feather-icon icon="MoreHorizontalIcon" size="21" />
              </div>
            </template>

            <b-dropdown-item
              v-if="$can('accounting.receivable.cohort.get')"
              @click="$router.push({ name: 'apps-accounting-receivable-cohort-view', params: { id: data.item.id } })"
            >
              <div class="d-flex align-items-center justify-content-between w-100">
                <span>Edit</span>
                <feather-icon icon="Edit3Icon" size="15" class="ml-50 text-muted" />
              </div>
            </b-dropdown-item>
          </b-dropdown>

          <!-- <div class="text-nowrap">
            <b-link :to="{ name: 'apps-accounting-receivable-cohort-edit', params: { id: data.item.id } }">
              <feather-icon :id="`cohort-row-${data.item.id}-edit-icon`" icon="EditIcon" class="cursor-pointer" size="16" />
              <b-tooltip title="Edit Cohort" class="cursor-pointer" :target="`cohort-row-${data.item.id}-edit-icon`" />
            </b-link>

            <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
              <template #button-content>
                <feather-icon icon="MoreVerticalIcon" size="16" class="align-middle text-body" />
              </template>

              <b-dropdown-item :to="{ name: 'apps-accounting-receivable-cohort-edit', params: { id: data.item.id } }">
                <feather-icon icon="FileTextIcon" />
                <span class="align-middle ml-50">Edit</span>
              </b-dropdown-item>
              <b-dropdown-item @click="archiveCohort(data.item.id)">
                <feather-icon icon="ArchiveIcon" />
                <span class="align-middle ml-50">Archive</span>
              </b-dropdown-item>
            </b-dropdown>
          </div> -->
        </template>
      </b-table>
      <div class="mx-2 mb-50">
        <b-row>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted">Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries</span>
          </b-col>
          <!-- Pagination -->
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalCohorts"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BTooltip,
} from 'bootstrap-vue';
import vSelect from 'vue-select';
import store from '@/store';
import { ref, onUnmounted } from '@vue/composition-api';
import { avatarText, title } from '@core/utils/filter';
import CohortListFilters from './CohortListFilters.vue';
import useCohortsList from './useCohortsList';
import CohortListAddNew from './CohortListAddNew.vue';
import moment from 'moment';
import usePathwayCohorts from '@/views/apps/roster/pathways/cohorts/usePathwayCohorts';
import useCourseSections from '@/views/apps/roster/courses/sections/useCourseSections';

export default {
  components: {
    CohortListFilters,
    CohortListAddNew,

    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BTooltip,

    vSelect,
  },
  data: () => {
    return { isAddSessionOpen: false };
  },
  methods: {
    money(v) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(v);
    },
    startSessionSelect(cohort) {
      this.sessionCohort = cohort;
      this.isAddSessionOpen = true;
    },
    archiveCohort(cohortId) {
      this.$swal({
        title: 'Are you sure?',
        text: 'Are you sure you want to archive this cohort?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Remove',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(async (result) => {
        if (result.value) {
          store
            .dispatch('receivableStoreModule/updateCohort', {
              id: cohortId,
              cohortData: {
                status: 'archived',
              },
            })
            .then((response) => {
              this.refetchData();
            });
        }
      });
    },
  },
  mounted() {
    if (this.$route.query.op === 'create') {
      this.isAddNewCohortSidebarActive = true;
    }
  },
  metaInfo() {
    return {
      title: 'AR Cohorts',
    };
  },
  setup() {
    const isAddNewCohortSidebarActive = ref(false);

    const {
      fetchCohorts,
      tableColumns,
      perPage,
      currentPage,
      totalCohorts,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refCohortListTable,
      refetchData,

      // UI

      resolveCohortStatusVariant,
      resolveCohortYNVariant,
      // Extra Filters
      statusFilter,
      statusOptions,

      createAtStartFilter,
      createAtEndFilter,
    } = useCohortsList();

    const { resolvePathwayCohortName } = usePathwayCohorts();

    const { resolveCourseSectionName } = useCourseSections();

    return {
      // Sidebar
      isAddNewCohortSidebarActive,

      resolvePathwayCohortName,
      resolveCourseSectionName,

      fetchCohorts,
      tableColumns,
      perPage,
      currentPage,
      totalCohorts,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refCohortListTable,
      refetchData,

      // Filter
      avatarText,
      title,

      // UI
      resolveCohortStatusVariant,
      resolveCohortYNVariant,

      // Extra Filters
      statusFilter,
      statusOptions,

      createAtStartFilter,
      createAtEndFilter,

      //
      moment,
    };
  },
};
</script>

<style scoped lang="scss">
.per-page-selector {
  width: 90px;
}
</style>
